import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import StripeCheckout from "react-stripe-checkout";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

import Typography from "@mui/material/Typography";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";

import Button from "../../components/Button";
import { PageHeader, BasePaper, Row, Text, FlexRow } from "../../components/Base";
import StyledTable from "../../components/TableComponent";
import BillingAddressSelector from "../billing-form";
import { useShoppingCart } from "../../context/ShoppingCartContext";
import {
  calculatePrice,
  payWithStripe,
  createPayWithInvoice,
  getAlreadyPurchasedConflicts,
  getUserBoughtCourses,
} from "../../api/payment";
import Service from "./service";
import ShoppingCartService from "../../services/payments";
import { useConfirm } from "../../context/ConfirmContext";
import { usePubsub } from "../../hooks/usePubsub";
import { getMyInfo } from "../../api/user";
import { getBillingAddress } from "../../api/user.deprecated";

const ShoppingCart = ({ match }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { crewId } = match.params;
  const { items, actions } = useShoppingCart();
  const [shoppingItems, setShoppingItems] = useState(null);
  const [total, setTotal] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [userNames, setUserNames] = useState([]);
  const [userPayWithInvoice, setUserPayWithInvoice] = useState(0);
  const [selectedBillingId, setSelectedBillingId] = useState(null);
  const [billingEmail, setBillingEmail] = useState(null);
  const [additionalBillingEmail, setAdditionalBillingEmail] = useState(null);
  const dialog = useConfirm();

  const [open, setOpen] = useState(false);

  // CREW-559: state of the background already-purchased check run when the
  // trolley is shown. It never blocks invoice/Stripe; only PayPal waits for it
  // (PayPal captures money before our API could reject with 409).
  const [purchasedCheckState, setPurchasedCheckState] = useState("idle"); // idle | running | done | failed

  const [refresh, setRefresh] = useState(0);

  const forceRefresh = () => setRefresh((r) => r + 1);

  const publishableKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  const style = { layout: "horizontal", color: "silver" };

  const handleClose = () => {
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(true);
  };

  usePubsub("billing/address-changed", forceRefresh);

  // CREW-559: drop trolley items the server reports as already purchased and
  // tell the user; they decide whether to pay for the rest or keep shopping.
  const removeAlreadyPurchased = async (conflicts) => {
    const conflictedItems = items.filter((item) =>
      conflicts.some(
        (c) => c.courseId === item.courseId && c.userId === item.userId && !!c.isBundle === !!item.isBundle,
      ),
    );
    if (!conflictedItems.length) {
      return;
    }
    for (const item of conflictedItems) {
      await actions.remove(item);
    }
    const removedCourses = conflictedItems.map((item) => `(${item.courseId}) ${item.courseName}`).join(", ");
    dialog.notification(
      t("shopping_cart.already_purchased_title"),
      t("shopping_cart.already_purchased_message", { courses: removedCourses }),
    );
    forceRefresh();
  };

  // CREW-559: conflicts computed from the existing bought-products endpoint -
  // the same data source the server-side guard uses (getUserProducts).
  const findConflictsViaBoughtProducts = async (itemsToCheck) => {
    const pairs = new Map();
    for (const item of itemsToCheck) {
      const pairCrewId = item.crewId || crewId;
      pairs.set(`${item.userId}:${pairCrewId}`, { userId: item.userId, pairCrewId });
    }
    const productsByPair = new Map();
    await Promise.all(
      [...pairs.entries()].map(async ([key, pair]) => {
        productsByPair.set(key, await getUserBoughtCourses(pair.userId, pair.pairCrewId));
      }),
    );
    return itemsToCheck
      .filter((item) => {
        const products = productsByPair.get(`${item.userId}:${item.crewId || crewId}`);
        return item.isBundle ? products?.bundles?.includes(item.courseId) : products?.courses?.includes(item.courseId);
      })
      .map((item) => ({ courseId: item.courseId, userId: item.userId, isBundle: !!item.isBundle }));
  };

  useEffect(() => {
    const crewCourses = ShoppingCartService.getCrewItems(crewId);
    setShoppingItems(crewCourses);
  }, [crewId, refresh]);

  // CREW-559: background duplicate check when the trolley is shown. Removes
  // already purchased items with a notification once it finishes; never blocks
  // submitting a payment while running or after failing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!items?.length) {
        setPurchasedCheckState("done");
        return;
      }
      setPurchasedCheckState("running");
      try {
        const conflicts = await findConflictsViaBoughtProducts(items);
        if (cancelled) {
          return;
        }
        if (conflicts.length) {
          await removeAlreadyPurchased(conflicts);
        }
        setPurchasedCheckState("done");
      } catch (error) {
        if (!cancelled) {
          console.warn("Shopping cart purchased-check failed.", error);
          setPurchasedCheckState("failed");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [items]);

  useEffect(() => {
    (async () => {
      if (items?.length) {
        const userData = await getMyInfo();
        setUserPayWithInvoice(userData.payWithInvoice);

        const address = await getBillingAddress(selectedBillingId);
        if (address) {
          setBillingEmail(address.email);
          setAdditionalBillingEmail(address.additionalEmail);
        }

        const response = await calculatePrice(items, selectedBillingId);

        const shoppingItems = Service.mapToDisplayObject(
          items,
          response.prices,
          response.bundlePrices,
          response.currency,
          response.names,
        );
        setTotal(response.total);
        setCurrency(response.currency);
        setShoppingItems(shoppingItems);
        setUserNames(response.names);
      }
    })();
  }, [items, refresh]);

  const payPalExecute = async (details) => {
    const response = await calculatePrice(items, selectedBillingId);
    const shoppingItems = Service.mapToDisplayObject(
      items,
      response.prices,
      response.bundlePrices,
      response.currency,
      response.names,
    );
    setTotal(response.total);
    setCurrency(response.currency);
    setShoppingItems(shoppingItems);
    setUserNames(response.names);

    handleToggle();
    if (details.status === "COMPLETED") {
      try {
        const retVal = await createPayWithInvoice(items, selectedBillingId, shoppingItems, "paypal", "paid");
        if (retVal.orderStatus === "errorInvoice") {
          handleClose();
          dialog.notification(
            t("shopping_cart.order_not_complete_title"),
            t("shopping_cart.order_not_complete_message"),
          );
          history.push(`/order-paid=2`);
        } else if (retVal.orderStatus === "success") {
          handleClose();
          await actions.clear();
          history.push(`/order-paid=1`);
        } else {
          handleClose();
          await actions.clear();
          history.push(`/order-paid=0`);
        }
      } catch (error) {
        handleClose();
        dialog.notification(t("shopping_cart.order_error_title"), t("shopping_cart.invoice_error_after_payment"));
        history.push(`/order-paid=0`);
      }
    } else {
      handleClose();
      dialog.notification(t("shopping_cart.order_error_title"), t("shopping_cart.paypal_not_completed"));
      history.push(`/order-paid=0`);
    }
  };

  const payNow = async (token) => {
    if (open) {
      return;
    }
    try {
      handleToggle();
      const response = await payWithStripe(token, total, currency, items, selectedBillingId, shoppingItems, "stripe");
      if (response.status === "success") {
        handleClose();
        await actions.clear();
        history.push(`/order-paid=1`);
      } else if (response.status === "error-invoice") {
        handleClose();
        await actions.clear();
        history.push(`/order-paid=2`);
      } else if (response.status === "failure") {
        handleClose();
        await actions.clear();
        history.push(`/order-paid=0`);
      }
    } catch (error) {
      handleClose();
      const conflicts = await getAlreadyPurchasedConflicts(error);
      if (conflicts) {
        // CREW-559: nothing was charged - drop the already purchased items and
        // let the user decide about the rest.
        await removeAlreadyPurchased(conflicts);
        return;
      }
      await actions.clear();
      history.push(`/order-paid=0`);
    }
  };

  return (
    <>
      <PageHeader>{t("shopping_cart.title")}</PageHeader>
      {shoppingItems?.length ? (
        <BasePaper>
          <Row>
            <Typography variant="h5">{t("shopping_cart.items")}</Typography>
          </Row>
          <Row>
            <StyledTable
              items={shoppingItems || []}
              renderChildren={(item) => (
                <TableRow key={item.courseId}>
                  <TableCell>
                    ({item.courseId}) {item.courseName} {item.isBundle ? ` (${t("shopping_cart.bundle")})` : ""}
                  </TableCell>
                  <TableCell>
                    ({item.userId}) {userNames.find((n) => n.id == item.userId)?.fullname}
                  </TableCell>
                  <TableCell>{item.price}</TableCell>
                  <TableCell>
                    <Button
                      icon={<DeleteIcon />}
                      title={t("shopping_cart.remove_from_cart")}
                      onClick={async () => {
                        await actions.remove(item);
                        forceRefresh();
                      }}
                    />
                  </TableCell>
                </TableRow>
              )}
            />
          </Row>
          <BillingAddressSelector
            onSelect={(selected) => {
              setSelectedBillingId(selected);
              forceRefresh();
            }}
          />
          <Row>
            {t("shopping_cart.billing_email")}: {billingEmail}
          </Row>
          <Row>
            {t("shopping_cart.additional_billing_email")}: {additionalBillingEmail}
          </Row>
          <BasePaper>
            <Row>
              <Typography variant="h5">{t("shopping_cart.payment")}</Typography>
            </Row>

            <Row>
              <FlexRow>
                <div>
                  <div style={{ marginBottom: "12px" }}>
                    {userPayWithInvoice == 1 && (
                      <Button //userPayWithInvoice
                        disabled={open}
                        onClick={async () => {
                          if (open) {
                            return;
                          }
                          handleToggle();
                          if (!selectedBillingId) {
                            handleClose();
                            dialog.notification(
                              t("shopping_cart.billing_address_required"),
                              t("shopping_cart.please_specify_billing_address"),
                            );
                            return;
                          }
                          try {
                            const retVal = await createPayWithInvoice(
                              items,
                              selectedBillingId,
                              shoppingItems,
                              "invoice",
                              "processing",
                            );
                            //const retVal = await actions.payWithInvoice(selectedBillingId, shoppingItems, userNames);

                            if (retVal === false || retVal.orderStatus == "failure") {
                              handleClose();
                              dialog.notification(
                                t("shopping_cart.order_error_title"),
                                t("shopping_cart.order_error_message"),
                              );
                              history.push(`/order-paid=0`);
                            } else {
                              handleClose();
                              await actions.clear();
                              history.push(`/order-paid=1`);
                            }
                          } catch (error) {
                            handleClose();
                            const conflicts = await getAlreadyPurchasedConflicts(error);
                            if (conflicts) {
                              await removeAlreadyPurchased(conflicts);
                            } else if (error.isTimeout || error.status === 504) {
                              dialog.notification(
                                t("shopping_cart.request_timeout_title"),
                                t("shopping_cart.request_timeout_message"),
                              );
                            } else {
                              dialog.notification(
                                t("shopping_cart.order_error_title"),
                                t("shopping_cart.order_error_message"),
                              );
                            }
                          }
                        }}
                      >
                        {t("shopping_cart.pay_with_invoice")}
                      </Button>
                    )}

                    <Backdrop
                      sx={{
                        color: "#fff",
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                      }}
                      open={open}
                    >
                      <CircularProgress color="inherit" />
                    </Backdrop>
                    <Button
                      onClick={async () => {
                        await actions.clear();
                        forceRefresh();
                      }}
                    >
                      {t("shopping_cart.clear_cart")}
                    </Button>
                  </div>

                  <div>
                    <StripeCheckout
                      stripeKey={publishableKey}
                      label={t("shopping_cart.pay_with_credit_card")}
                      name={t("shopping_cart.pay_with_credit_card_label")}
                      amount={total * 100}
                      currency={currency}
                      email={billingEmail}
                      description={`${t("shopping_cart.your_total_is")} ${currency} ${total}`}
                      token={payNow}
                    />
                  </div>
                  <div
                    style={{
                      maxWidth: "750px",
                      minHeight: "200px",
                      paddingTop: "12px",
                    }}
                  >
                    {/* CREW-559: PayPal captures money before our API could answer 409,
                        so the button waits for the background purchased-check. */}
                    {purchasedCheckState !== "done" && purchasedCheckState !== "failed" && (
                      <FlexRow style={{ alignItems: "center", gap: "8px", paddingBottom: "8px" }}>
                        <CircularProgress size={18} />
                      </FlexRow>
                    )}
                    <PayPalScriptProvider options={{ "client-id": paypalClientId, currency: "CHF" }}>
                      <PayPalButtons
                        forceReRender={[total, currency]}
                        fundingSource="paypal"
                        style={style}
                        disabled={open || (purchasedCheckState !== "done" && purchasedCheckState !== "failed")}
                        createOrder={async (data, paypalActions) => {
                          // CREW-559: last re-check BEFORE PayPal captures money -
                          // a conflict found after capture could not be rejected.
                          const conflicts = await findConflictsViaBoughtProducts(items).catch(() => []);
                          if (conflicts.length) {
                            await removeAlreadyPurchased(conflicts);
                            throw new Error("already_purchased");
                          }
                          return paypalActions.order.create({
                            purchase_units: [
                              {
                                description: t("shopping_cart.ngft_invoice"),
                                amount: {
                                  currency_code: currency,
                                  value: total,
                                },
                              },
                            ],
                          });
                        }}
                        onApprove={(data, actions) => {
                          return actions.order.capture().then((details) => {
                            payPalExecute(details);
                          });
                        }}
                      />
                    </PayPalScriptProvider>
                  </div>
                </div>

                <Typography variant="h4">
                  <Text>
                    {t("shopping_cart.total")}: {`${currency} ${total}`}
                  </Text>
                </Typography>
              </FlexRow>
            </Row>
          </BasePaper>
        </BasePaper>
      ) : (
        <Typography variant="h4">{t("shopping_cart.empty_cart")}</Typography>
      )}
    </>
  );
};

export default ShoppingCart;
